import ApiService from "../../../core/service/api-service";
import { useTranslation } from "react-i18next";
import DataGrid, { RenderableExpandRow } from "../../../core/components/data-grid/DataGrid";
import { useEffect, useRef, useState } from "react";
import { DataGridProvider, useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import studyGridColumns from "./StudyColumns";
import StudyGridTools from "./StudyGridTools";

import './row-styles.css'
import StudyAddRequest from "../study-add-request/StudyAddRequest";
import { useModal } from "../../../core/context/ModalContext";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import Colors from "../../../core/style/Colors";
import { useSignalR } from "../../../core/context/SignalRContext";
import useFocusListener from "../../../core/components/FocusListener";
import { Modal, Tabs, Tooltip, Popover, Typography } from 'antd'
import { useApp } from "../../../core/context/AppContext";
import { EyeOutlined, AlertOutlined } from "@ant-design/icons";
import { BsFillMicFill } from "react-icons/bs";
import { HiOutlineDocumentText } from 'react-icons/hi';
import { BsFileArrowDownFill } from 'react-icons/bs';
import Flex from "../../../core/components/Flex";
import EnumHelper from "../../../enums/EnumHelper";
import StudyRequestGrid from "../../request/request-data-grid/StudyRequestGrid";
import VoiceRecordGrid from "../../voice-record/voice-record-grid/VoiceRecordGrid";
import StudyFileGrid from "../study-files/grid/StudyFileGrid";
import StudyTypeEnum from "../../../enums/StudyTypeEnum";
import StudyStatusEnum from "../../../enums/StudyStatusEnum";
import StudyHelper from "../StudyHelper";
import ReportModal from "../../../pages/report/ReportModal";
import { getBeginLocalStr, getBeginStr, getEndLocalStr, getEndStr } from "../../../core/utils/date-utils";
import StudyLogsGrid from "../StudyLogsGrid";
import StudyTasksGrid from "../StudyTasksGrid";
import SeriesMetadataTable from "../SeriesMetadataTable";
import StudyRowMenu from "./StudyRowMenu";
import InstitutionSelect from "../../user/InstitutionSelect";
import StudyAutoExportHistoryGrid from "../study-auto-export-history/StudyAutoExportHistoryGrid";

export default function StudyGrid({ }) {
    const { t } = useTranslation();
    const { setDataGridProps, queryModel, setData, setPaginationResult, setRowColor, selectedRows, setSelectedRows, refreshDataGrid, renderExpandedRow, onFocusOut, tableId } = useDataGrid()
    const { openModal, closeModal, modal } = useModal()
    const { user, hasPermission } = useUser()
    const { onUpdateStudyGrid, hubContext } = useSignalR()
    const { addKeyEvent, removeKeyEvent, pressedKey } = useApp()
    const isFocus = useFocusListener({ elementId: tableId })

    const initDataGrid = async () => {
        const _qm = {
            ...queryModel
        }

        let sortItems = queryModel.orderByItems
        if (_qm.filter.studyTypeList && _qm.filter.studyTypeList.find(x => x === StudyTypeEnum.EMERGENCY)) {
            if (queryModel.orderByItems?.length === 0) {
                sortItems = [
                    {
                        name: 'studyDetail.timeoutDate', operation: 2
                    }
                ]
            }
        }
        _qm.orderByItems = sortItems;
        _qm.filter = {
            ..._qm.filter,
            studyReceivedDateTimeBegin: getBeginStr(_qm.filter.studyReceivedDateTimeBegin),
            studyReceivedDateTimeEnd: getEndStr(_qm.filter.studyReceivedDateTimeEnd),
            studyDateTimeBegin: getBeginLocalStr(_qm.filter.studyDateTimeBegin),
            studyDateTimeEnd: getEndLocalStr(_qm.filter.studyDateTimeEnd),
            patientIdList: (_qm.filter.patientId && _qm.filter.patientId.split(',')) || [],
        };
        const response = await ApiService.Study.DataGrid(_qm);
        setData(response.data)
        setPaginationResult(response.pagination)
    }

    const selectedStudy = useRef(null)
    const selectedReq = useRef(null)

    useEffect(() => {
        console.log('[StudyGrid] useEffect 1: onUpdateStudyGrid changed', { hasPermission: hasPermission(PERMISSIONS.HOSPITAL_GROUP_FILTER), onUpdateStudyGridKey: onUpdateStudyGrid?.key });
        if (hasPermission(PERMISSIONS.HOSPITAL_GROUP_FILTER) && onUpdateStudyGrid?.key) {
            initDataGrid()
        }
    }, [onUpdateStudyGrid])

    useEffect(() => {
        // console.log('[StudyGrid] useEffect 2: addKeyEvent');
        addKeyEvent([
            { keyCode: 'KeyR', modifier: null, prevent: false, },
            { keyCode: 'KeyA', modifier: null, prevent: false, },
        ])
    }, []);

    useEffect(() => {
        // console.log('[StudyGrid] useEffect 3: pressedKey changed', { isFocus, pressedKey, selectedStudyId: selectedStudy.current?.id });
        if (!isFocus)
            return

        if (!selectedStudy.current?.id)
            return

        const element = document.activeElement;
        if (element.tagName === 'INPUT')
            return

        if (!pressedKey?.modifier?.code) {
            switch (pressedKey?.key?.code) {
                case 'KeyA': {
                    addRequest()
                    break
                }
                case 'KeyR': {
                    if (!hasPermission(PERMISSIONS.REPORT_ADD_OR_EDIT))
                        break
                    if (StudyHelper.openReqRepPage(selectedReq.current, selectedStudy.current)) {
                        openModal(t('Report'), (
                            <ReportModal
                                studyId={selectedStudy.current?.id}
                                requestId={selectedReq.current?.id}
                            />
                        ), null, window.innerWidth * 0.98, 20)
                    }
                    else {

                    }
                }
            }
        }
    }, [pressedKey]);

    const addRequest = async () => {


        openModal(t('Add Request & Anamnez'), (
            <StudyAddRequest
                studyRow={selectedStudy.current}
                onSaved={async () => {
                    refreshDataGrid()
                    renderExpandedRow(selectedStudy.current.id)
                    closeModal()
                }}
                studyId={selectedStudy.current.id}
            />
        ))
    }


    useEffect(() => {
        if (window.studyGridKey !== queryModel.updateKey) {
            window.studyGridKey = queryModel.updateKey
            initDataGrid()
        }
    }, [queryModel.updateKey])

    useEffect(() => {
        const columns = studyGridColumns(refreshDataGrid, hasPermission)
        setDataGridProps({
            rowKey: 'id',
            columns: columns,
            enableRowSelection: true,
            antdTableProps: {
                scroll: { x: columns.map(x => x.width || 0).reduce((a, b) => a + b) },
                tableLayout: 'fixed'
            },
            rowExpandable: record => true,
            expandedRowRender: (record, index, indent, expanded) => {
                return (
                    <RenderableExpandRow expanded={expanded} recordId={record.id}>
                        <Tabs defaultActiveKey="1" style={{ backgroundColor: '#fff', padding: 8 }}>
                            {
                                hasPermission(PERMISSIONS.STUDY_REQUEST_LIST) && (
                                    <Tabs.TabPane tab={t('Requests')} key="1">
                                        <DataGridProvider name={'study-request-provider'} defaultFilter={{}}>
                                            <StudyRequestGrid
                                                studyId={record.id}
                                                onSelected={(req) => {
                                                    if (record.id !== selectedStudy?.current?.id)
                                                        selectStudyRow(record)

                                                    selectedReq.current = req
                                                }}
                                            />
                                        </DataGridProvider>
                                    </Tabs.TabPane>
                                )
                            }
                            {
                                hasPermission(PERMISSIONS.VOICE_RECORD_LIST) && (
                                    <Tabs.TabPane tab={t('Voice Records')} key="2">
                                        <DataGridProvider name={'study-voice-rec-provider'} defaultFilter={{}}>
                                            <VoiceRecordGrid studyId={record.id} />
                                        </DataGridProvider>
                                    </Tabs.TabPane>
                                )
                            }
                            {
                                hasPermission(PERMISSIONS.STUDY_FILE_LIST) && (
                                    <Tabs.TabPane tab={t('Files')} key="3">
                                        <DataGridProvider name={'study-file-provider'}>
                                            <StudyFileGrid studyId={record.id} />
                                        </DataGridProvider>
                                    </Tabs.TabPane>
                                )
                            }
                            <Tabs.TabPane tab={t('Dicom Data')}>
                                <SeriesMetadataTable studyId={record.id} />
                            </Tabs.TabPane>
                            {
                                hasPermission(PERMISSIONS.STUDY_LOG) && (
                                    <Tabs.TabPane tab={t('Study Logs')} key="4">
                                        <DataGridProvider>
                                            <StudyLogsGrid studyId={record.id} />
                                        </DataGridProvider>
                                    </Tabs.TabPane>
                                )
                            }
                            {
                                hasPermission(PERMISSIONS.STUDY_TASK_MANAGER) && (
                                    <Tabs.TabPane tab={t('Tasks')} key="5">
                                        <DataGridProvider>
                                            <StudyTasksGrid studyId={record.id} />
                                        </DataGridProvider>
                                    </Tabs.TabPane>
                                )
                            }
                            {
                                hasPermission(PERMISSIONS.AUTO_EXPORT_MANAGE) && (
                                    <Tabs.TabPane tab={t('Auto Export History')} key="6">
                                        <DataGridProvider name={'study-auto-export-history-provider'} defaultFilter={{}}>
                                            <StudyAutoExportHistoryGrid studyId={record.id} />
                                        </DataGridProvider>
                                    </Tabs.TabPane>
                                )
                            }
                        </Tabs>
                    </RenderableExpandRow>
                )
            },
            rowClassName: (record, index) => {
                let className = 'normal-row';

                if(!record.isReportable && !record.isDeleted) {
                    className = 'no-report-row'
                    return className;
                }

                if (record.isDeleted)
                    className = 'deleted-row ';
                else if (record.studyType === StudyTypeEnum.EMERGENCY) {
                    if (record.studyStatus === StudyStatusEnum.COMPLETED)
                        className = 'has-report-row';
                    else if (record.studyStatus === StudyStatusEnum.TIMEOUT_HAS_NOT_CONFIRMED_REPORT)
                        className = 'has-report-row';
                    else
                        className = 'emergency-row';
                }
                else {
                    if (record.studyStatus === StudyStatusEnum.WAITING) {
                        className = 'no-report-row'
                    }
                    else if (record.studyStatus === StudyStatusEnum.HAS_NOT_CONFIRMED_REPORT) {
                        className = 'not-confirmed-row';
                    }
                    else if (record.studyStatus === StudyStatusEnum.TIMEOUT_HAS_NOT_CONFIRMED_REPORT) {
                        className = 'has-report-row';
                    }
                    else if (record.studyStatus === StudyStatusEnum.COMPLETED) {
                        className = 'has-report-row';
                    }
                }

                return className;
            },
            onRow: (record) => {
                return {
                    onClick: (e) => {
                        selectStudyRow(record)
                    },
                    onDoubleClick: () => {

                    }
                }
            }
        })
    }, [])

    const selectStudyRow = (record) => {
        if (selectedStudy?.current?.id === record?.id) {
            selectedStudy.current = null;
            selectedReq.current = null;
            setRowColor(record.id, '');
            return
        }
        setRowColor(record.id, Colors.selectedRow);
        selectedStudy.current = record
        selectedReq.current = null
    }

    useEffect(() => {
        console.log('[StudyGrid] useEffect 6: onFocusOut changed', { selectedStudyId: selectedStudy.current?.id });
        if (selectedStudy.current) {
            setRowColor(selectedStudy.current.id, '');
            selectedStudy.current = null
        }
    }, [onFocusOut])

    return (
        <>
            <DataGrid
                title={() => (
                    <StudyGridTools />
                )}
                name={'study'}
                mobileCardTitle={(record) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 600, fontSize: 14 }}>{record.patientName}</span>
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <span style={{
                                    backgroundColor: record.studyType === StudyTypeEnum.EMERGENCY ? '#ff4d4f' : '#1890ff',
                                    color: '#fff',
                                    padding: '2px 6px',
                                    borderRadius: 4,
                                    fontSize: 11,
                                    fontWeight: 500
                                }}>
                                    {t(EnumHelper.getKeyByValue(StudyTypeEnum, record.studyType))}
                                </span>
                                <span style={{
                                    backgroundColor: '#1890ff',
                                    color: '#fff',
                                    padding: '2px 6px',
                                    borderRadius: 4,
                                    fontSize: 11,
                                    fontWeight: 500
                                }}>
                                    {record.modality}
                                </span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, fontSize: 12, color: '#666', alignItems: 'center' }}>
                            <StudyRowMenu row={record} />
                            <span style={{
                                backgroundColor: '#52c41a',
                                color: '#fff',
                                padding: '1px 6px',
                                borderRadius: 4,
                                fontSize: 11
                            }}>
                                {record.reportCount}/{record.requestCount}
                            </span>
                            <Flex alignItems={'center'} style={{ gap: 4 }}>
                                {record.isRead && (
                                    <Tooltip title={t('Read')}>
                                        <EyeOutlined style={{ color: '#2980b9' }} />
                                    </Tooltip>
                                )}
                                {record.isEmergency && (
                                    <Tooltip title={t('Emergency')}>
                                        <AlertOutlined style={{ color: '#c0392b' }} />
                                    </Tooltip>
                                )}
                                {record.hasVoiceRecord && (
                                    <Tooltip title={t('Voice Record')}>
                                        <BsFillMicFill />
                                    </Tooltip>
                                )}
                                {record.hasAnamnez && (
                                    <Tooltip title={t('Anamnez')}>
                                        <HiOutlineDocumentText />
                                    </Tooltip>
                                )}
                                {record.hasFile && (
                                    <Tooltip title={t('File')}>
                                        <BsFileArrowDownFill color={'#3742fa'} />
                                    </Tooltip>
                                )}
                            </Flex>
                        </div>
                        <div style={{ display: 'flex', gap: 8, fontSize: 12, color: '#888' }}>
                            <span>{t('Patient ID')}: {record.dicomPatientId}</span>
                            <span>{t('Accession Number')}: {record.accessionNumber}</span>
                        </div>
                        {hasPermission(PERMISSIONS.EDIT_STUDY_INSTITUTION) ? (
                            <Popover
                                trigger={'click'}
                                placement={'bottom'}
                                getPopupContainer={() => document.body}
                                content={(
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <InstitutionSelect
                                            value={record.institutionId}
                                            size={'small'}
                                            style={{ width: 150 }}
                                            placeholder={record.institution || '-'}
                                            allowClear={true}
                                            showSearch={true}
                                            mode={'single'}
                                            onChange={async (val) => {
                                                const res = await ApiService.Study.EditInstitutions(record.id, val)
                                                if (res.success) {
                                                    refreshDataGrid()
                                                }
                                            }} />
                                    </div>
                                )}
                            >
                                <Tooltip title={record.institution || '-'}>
                                    <Typography.Text
                                        ellipsis
                                        style={{ cursor: 'pointer' }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <a>{t('Institution')}: {record.institution || '-'}</a>
                                    </Typography.Text>
                                </Tooltip>
                            </Popover>
                        ) : (
                            <span>{t('Institution')}: {record.institution || '-'}</span>
                        )}
                    </div>
                )}
            />
        </>
    )
}
